import React, { useState, useEffect, useRef, useContext, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import apiClient from "../api/client";
import clsx from 'clsx';
import { toast } from 'sonner';
import {
  Video, VideoOff, Mic, MicOff, Send, Users, Clock, AlertTriangle,
  Shield, Crown, ArrowLeft, Eye, EyeOff, Volume2, VolumeX, UserX, Heart
} from 'lucide-react';
import { AuthContext } from '../App';

// Backend origin for absolute-path media URLs (audio attachments, etc.)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
import { useEntitlementsSafe } from '../context/EntitlementsContext';
import useSSE from '../hooks/useSSE';
import { subscribeToEvent } from '../lib/realtime';
import { markFlowStep } from '../utils/unityFlowStorage';
import { getRealtimeClient } from '../services/realtime';
// import { sendWebrtcSignal, subscribeWebrtcSignals } from '../lib/realtime'; // Now handled by useLoungeWebRTC
// import { createPeerConnection } from '../lib/webrtcClient'; // Now handled by useLoungeWebRTC
import { formatRemainingTime } from '../config/pricing';
import VideoBoostPanel from './VideoBoostPanel';
import { useLocalVideo } from '../hooks/useLocalVideo';
import { useLoungeWebRTC } from '../hooks/useLoungeWebRTC';
import { LoungeVideoLayout } from './LoungeVideoLayout';
import { useMessageReactions } from '../hooks/useMessageReactions';
import { useAudioRecorder } from '../hooks/useAudioRecorder';
import { reportUser, blockUser, kickFromLounge } from '../api/moderation';
import { api } from '../api/client';  // Centralized API client for consistent auth
import { goVerify } from '../utils/verificationNav';
import { useLoungeTime } from '../hooks/useLoungeTime';
import { useLoungeSession } from '../hooks/useLoungeSession';  // Phase 1: Foundation
import { useLoungeReactions, REACTION_TYPES } from '../hooks/useLoungeReactions';  // Phase 2: Reactions
import { useLoungeMeta } from '../hooks/useLoungeMeta';  // Phase 3: Time & Streak Badges
import LoungeGiftButton from './LoungeGiftButton';  // Ticket 27
import LoungeGiftToast from './LoungeGiftToast';    // Ticket 27
import { useGiftAnimations } from '../hooks/useGiftAnimations';  // Ticket 27
import ProfileAvatar from './ProfileAvatar';  // Unified avatar display
import { ProfileViewModal } from './profile/ProfileViewModal';  // P2
import { useProfileViewer } from '../hooks/useProfileViewer';    // P2
import { getAgeFromDob } from '../utils/ageCalculator';          // P2
import { REACTIONS } from './reactions/reactionsConfig';
import { useLoungeChatRequests } from '../hooks/useLoungeChatRequests';  // Request Chat V1
import ChatRequestModal from './ChatRequestModal';  // Request Chat V1
import { PostEventReflectionModal } from './reflection/PostEventReflectionModal';  // Post-Event Reflection
import {
  LoungeBackground,
  LoungeAvatar,
  TimeBadge,
  StreakBadge,
  LoungeControlBar,
  ReactionBubble,
} from './lounge';  // Afrofuturist Neon Design System

// IMPORTANT: Always fall back to window.location.origin in production.
// Some deploy targets don't inject REACT_APP_* vars at runtime.

// UUID detection: names that are actually IDs
const UUID_REGEX = /^[0-9a-f-]{30,}$/i;


// Ticket 22: Multi-Room Lounge - Fixed Room Configuration
const LOUNGE_ROOMS = {
  flirty: {
    key: "flirty",
    label: "Friendly Lounge",
    emoji: "💬",
    description: "Lighthearted conversations and friendly connections.",
    allowVideo: true,
  },
  after_dark: {
    key: "after_dark",
    label: "Night Owl",
    emoji: "🌙",
    description: "Evening conversations for night owls. Relaxed, candid discussions.",
    allowVideo: true,
  },
  deep_talks: {
    key: "deep_talks",
    label: "Deep Talks",
    emoji: "🧠",
    description: "Thoughtful, meaningful conversations.",
    allowVideo: true,
  },
  global: {
    key: "global",
    label: "Global Lounge",
    emoji: "🌍",
    description: "Connect with people from around the world.",
    allowVideo: true,
  },
  voice_only: {
    key: "voice_only",
    label: "Voice Only",
    emoji: "🎤",
    description: "Audio-first connections, camera off.",
    allowVideo: false, // 🔧 FIX: Voice-only room disables camera
  },
};


// Ticket 8: Animated typing indicator component
const TypingIndicator = () => {
  return (
    <div className="chat-typing-indicator text-xs text-white/80">
      <span className="mr-1">Typing</span>
      <span className="flex items-center gap-1">
        <span className="chat-typing-dot" />
        <span className="chat-typing-dot" />
        <span className="chat-typing-dot" />
      </span>
    </div>
  );
};

// Helper: Format time for short display
const formatTimeShortHelper = (seconds) => {
  if (seconds == null) return "--";
  if (seconds <= 0) return "0m";

  const mins = Math.floor(seconds / 60);
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;

  if (hrs > 0) return `${hrs}h ${remMins}m`;
  return `${mins}m`;
};

// Low-Time Toast Component
const LowTimeToast = ({ remainingSeconds }) => {
  if (!remainingSeconds || remainingSeconds <= 0) return null;

  return (
    <div className="fixed bottom-20 inset-x-4 z-40 md:bottom-6 md:left-auto md:right-6 md:w-80 lounge-fixed-bottom">
      <div className="bg-amber-500 text-white rounded-xl px-4 py-3 shadow-lg flex items-center space-x-3 animate-pulse">
        <div className="text-xl">⏳</div>
        <div className="flex-1">
          <p className="font-semibold text-sm">
            You're running low on time
          </p>
          <p className="text-xs text-amber-50">
            About {formatTimeShortHelper(remainingSeconds)} of video time left for today.
          </p>
        </div>
      </div>
    </div>
  );
};

// Out-of-Time Paywall Modal
const OutOfTimeModal = ({
  remainingSeconds,
  onClose,
  onBuyBoost1h,
  onBuyBoost4h,
  onGoPremium,
  onGoElite,
  isPurchasing,
}) => {
  const disabled = isPurchasing;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 border border-pink-500/40">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white text-lg font-semibold flex items-center space-x-2">
            <span className="text-pink-400 text-xl">❤️</span>
            <span>Out of Daily Time</span>
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-200 text-sm"
            disabled={isPurchasing}
          >
            ✕
          </button>
        </div>

        <p className="text-gray-300 text-sm mb-4">
          You've used your daily video time for today.
          Keep your vibe going with a Time Boost or upgrade your plan.
        </p>

        <div className="space-y-3 mb-4">
          {/* 1 Hour Boost */}
          <button
            onClick={onBuyBoost1h}
            disabled={disabled}
            className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border text-sm ${
              disabled
                ? "opacity-60 cursor-not-allowed border-gray-700 text-gray-400"
                : "border-pink-500/60 text-white hover:bg-pink-600/10"
            }`}
          >
            <div className="flex flex-col items-start">
              <span className="font-semibold">⚡ 1 Hour Boost</span>
              <span className="text-xs text-gray-400">
                Perfect for a quick session
              </span>
            </div>
            <span className="font-bold text-pink-300">$2.99</span>
          </button>

          {/* 4 Hour Boost */}
          <button
            onClick={onBuyBoost4h}
            disabled={disabled}
            className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border text-sm relative overflow-hidden ${
              disabled
                ? "opacity-60 cursor-not-allowed border-gray-700 text-gray-400"
                : "border-amber-500/70 text-white hover:bg-amber-500/10"
            }`}
          >
            <div className="absolute top-0 right-0 px-2 py-0.5 bg-amber-500 text-[10px] text-black font-semibold rounded-bl-xl">
              BEST VALUE
            </div>
            <div className="flex flex-col items-start">
              <span className="font-semibold">⚡ 4 Hour Boost</span>
              <span className="text-xs text-gray-400">
                Great for deep conversations
              </span>
            </div>
            <span className="font-bold text-amber-300">$10.00</span>
          </button>
        </div>

        <div className="border-t border-gray-800 pt-3 mt-2">
          <p className="text-xs text-gray-400 mb-2">Or upgrade your plan:</p>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <button
              onClick={onGoPremium}
              disabled={disabled}
              className="text-xs bg-pink-600/80 hover:bg-pink-600 text-white rounded-lg px-3 py-2 font-semibold disabled:opacity-60 transition-colors"
            >
              Premium • $29.99/mo
            </button>
            <button
              onClick={onGoElite}
              disabled={disabled}
              className="text-xs bg-purple-600/80 hover:bg-purple-600 text-white rounded-lg px-3 py-2 font-semibold disabled:opacity-60 transition-colors"
            >
              Elite • $19.99/mo
            </button>
          </div>
          <p className="text-[10px] text-gray-500 text-center">
            Time resets daily at midnight. Boosts are one-time add-ons.
          </p>
        </div>

        {isPurchasing && (
          <div className="mt-3 text-center text-xs text-gray-400">
            Processing your boost… ⏳
          </div>
        )}
      </div>
    </div>
  );
};

// Normalize any incoming lounge message (from API or Ably)
function normalizeLoungeMessage(msg) {
  const id =
    msg.id ||
    msg.messageId ||
    msg._id ||
    `${msg.user_id || msg.senderId || "unknown"}:${msg.timestamp || msg.createdAt || ""}:${msg.content || msg.text || ""}`;

  const rawName =
    msg.senderName ||
    msg.first_name ||
    msg.profileName ||
    msg.name ||
    "";

  const isUuidName = UUID_REGEX.test(rawName);
  const displayName = isUuidName ? null : rawName;

  return {
    id,
    text: msg.text || msg.content || "",
    senderId: msg.senderId || msg.user_id || null,
    displayName,
    rawName,
    timestamp: msg.createdAt || msg.timestamp || new Date().toISOString(),
  };
}

const LifestyleLounge = () => {
  console.log('🔥 LifestyleLounge COMPONENT RENDERED');
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Entitlements for verification checks
  const { isVerified } = useEntitlementsSafe();
  const userIsAgeVerified = isVerified('age_verified');

  // Define currentUserId early so it's available for all hooks and effects
  const currentUserId = user?._id || user?.id || user?.user_id || user?.email || null;

  // P2: Profile viewer modal
  const profileViewer = useProfileViewer();

  // Ticket 22: Active room state
  const [activeRoomKey, setActiveRoomKey] = useState("flirty");
  const [isSwitchingRoom, setIsSwitchingRoom] = useState(false);
  const activeRoom = LOUNGE_ROOMS[activeRoomKey] || LOUNGE_ROOMS.flirty; // 🛡️ Fallback to prevent undefined
  const loungeMatchId = `lounge:${activeRoomKey}`;
  
  // Ticket 20: Lounge time tracking
  const {
    entitlements,
    remainingSeconds: loungeRemainingSeconds,
    limitReached,
    isElite
  } = useLoungeTime(activeRoomKey);
  const [showLoungeLimitModal, setShowLoungeLimitModal] = useState(false);
  
  // Phase 1: Centralized session management
  // CRITICAL: Memoize callbacks to prevent infinite re-render loop
  const onJoinSuccessCallback = useCallback(async (status) => {
    // Called after successful join - load participants and messages
    // Backend returns remaining_seconds_today (new) and remaining_seconds (alias).
    const rs =
      typeof status?.remaining_seconds_today === "number"
        ? status.remaining_seconds_today
        : (typeof status?.remaining_seconds === "number" ? status.remaining_seconds : null);
    setRemainingSeconds(rs);
    setShowOutOfTimeModal(false);
    // Note: loadParticipants and loadMessages are defined later, call them separately
  }, []);

  const onJoinErrorCallback = useCallback((error, detail) => {
    // Handle error states
    if (detail?.code === 'LOUNGE_DAILY_LIMIT_REACHED') {
      setShowLoungeLimitModal(true);
    } else if (detail?.code === 'OUT_OF_TIME') {
      setShowOutOfTimeModal(true);
    }
  }, []);

  const onLeaveSuccessCallback = useCallback(async (status) => {
    const rs =
      typeof status?.remaining_seconds_today === "number"
        ? status.remaining_seconds_today
        : (typeof status?.remaining_seconds === "number" ? status.remaining_seconds : null);
    setRemainingSeconds(rs);
  }, []);

  const loungeSession = useLoungeSession({
    token,
    activeRoomKey,
    onJoinSuccess: onJoinSuccessCallback,
    onJoinError: onJoinErrorCallback,
    onLeaveSuccess: onLeaveSuccessCallback,
  });
  
  // Extract from session hook (replaces old state)
  const { 
    isInLounge: inLounge,
    isJoining,
    loungeStatus: sessionLoungeStatus,
    error: sessionError,
    joinLounge: sessionJoinLounge,
    leaveLounge: sessionLeaveLounge,
    registerCleanup,
  } = loungeSession;

  /**
   * 🔧 FIX: Room Switching with Proper Cleanup
   * 
   * Issue: When users switch rooms, they're never removed from old room's pairing queue
   * This causes them to be unable to match in any room after switching
   * 
   * Solution: Before switching, leave the old room to clear queue state
   */
  const switchToRoom = useCallback(async (newRoomKey) => {
    if (newRoomKey === activeRoomKey || isSwitchingRoom) {
      return; // Already in this room or switching in progress
    }
    
    setIsSwitchingRoom(true);
    
    try {
      // Only call leave if we're actually in the lounge
      if (inLounge) {
        console.log(`[RoomSwitch] Leaving old room: ${activeRoomKey}`);
        
        // Call leave endpoint for current room to clear pairing queue
        await apiClient.post(
          `/lounge/leave?room_key=${activeRoomKey}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      // Switch to new room
      console.log(`[RoomSwitch] Switching to new room: ${newRoomKey}`);
      setActiveRoomKey(newRoomKey);
      
      // If we're in the lounge, rejoin with new room
      if (inLounge) {
        console.log(`[RoomSwitch] Rejoining with new room: ${newRoomKey}`);
        await apiClient.post(
          `/lounge/join?room_key=${newRoomKey}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      toast.success(`Switched to ${LOUNGE_ROOMS[newRoomKey].label}`, {
        duration: 2000,
        position: 'top-center',
      });
      
    } catch (error) {
      console.error('[RoomSwitch] Error switching rooms:', error);
      toast.error('Failed to switch rooms');
      // Rollback to old room on error
      setActiveRoomKey(activeRoomKey);
    } finally {
      setIsSwitchingRoom(false);
    }
  }, [activeRoomKey, inLounge, token, isSwitchingRoom]);

  
  // Legacy state (keeping for backward compatibility - will be removed later)
  const [loungeStatus, setLoungeStatus] = useState(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);
  const [remainingSeconds, setRemainingSeconds] = useState(null); // Server-provided remaining time
  
  // Sync session loungeStatus to local state for now (gradual migration)
  useEffect(() => {
    if (sessionLoungeStatus) {
      setLoungeStatus(sessionLoungeStatus);
    }
  }, [sessionLoungeStatus]);
  const [participants, setParticipants] = useState([]);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [typingUsers, setTypingUsers] = useState({});
  const [showParticipants, setShowParticipants] = useState(false); // Mobile participants toggle
  
  // Ticket 7: Smart scroll state
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [hasNewMessages, setHasNewMessages] = useState(false);
  
  // Media state
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [isAudioOn, setIsAudioOn] = useState(true);
  const [mutedUsers, setMutedUsers] = useState(new Set());
  const [hiddenVideos, setHiddenVideos] = useState(new Set());

  // Phase 4: Polish - Leave confirmation & first-time explainer
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [showTimeExplainer, setShowTimeExplainer] = useState(false);
  
  // Post-Event Reflection modal state
  const [showReflectionModal, setShowReflectionModal] = useState(false);
  const pendingLeaveRef = useRef(false);  // Track if we're in a leave flow
  const hasShownReflectionRef = useRef(false);  // Ensure modal shown only once per session

  // 🎯 PATCH: Video fullscreen state (viewport overlay, not browser fullscreen)
  const [isVideoFullscreen, setIsVideoFullscreen] = useState(false);
  const prevOverflowRef = useRef(null);  // Safer scroll restore

  // 🎯 PATCH: Prevent background scroll while video is fullscreen (safer restore)
  useEffect(() => {
    if (isVideoFullscreen) {
      // Save overflow only if we haven't already (prevents double-save)
      if (prevOverflowRef.current === null) {
        prevOverflowRef.current = document.body.style.overflow || '';
      }
      document.body.style.overflow = "hidden";
    } else {
      // Restore saved value and clear ref
      if (prevOverflowRef.current !== null) {
        document.body.style.overflow = prevOverflowRef.current;
        prevOverflowRef.current = null;
      }
    }
  }, [isVideoFullscreen]);

  // 🎯 PATCH: ESC key to exit video fullscreen
  useEffect(() => {
    if (!isVideoFullscreen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setIsVideoFullscreen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isVideoFullscreen]);

  // NOTE: Auto-exit fullscreen useEffect moved AFTER useLoungeWebRTC hook
  // (where isCameraOn is defined) to prevent render-time reference error

  // Phase 4: Check if user has seen time explainer
  useEffect(() => {
    if (inLounge) {
      const hasSeenExplainer = localStorage.getItem('lounge_seen_time_explainer_v1');
      if (!hasSeenExplainer) {
        // Show explainer after a brief delay (let UI settle)
        const timer = setTimeout(() => {
          setShowTimeExplainer(true);
        }, 2000);
        return () => clearTimeout(timer);
      }
    }
  }, [inLounge]);

  // Phase 4: Dismiss explainer and remember
  const dismissExplainer = () => {
    setShowTimeExplainer(false);
    localStorage.setItem('lounge_seen_time_explainer_v1', 'true');
  };

  // Phase 3: Time & Streak awareness badges (MOVED UP to fix hoisting issue)
  const {
    remainingMinutes,
    dailyLimitMinutes,
    isOutOfTime,
    isLowTime,
    streakLabel,
    streakProgress,
    streakMeta,
    loading: metaLoading,
  } = useLoungeMeta({ 
    token,
    enabled: inLounge, // Only fetch when in lounge
    pollMs: 30000, // Poll every 30s
  });

  // Phase 1: Use centralized leave with cleanup (MOVED UP to fix hoisting issue)
  // Note: Camera handlers will be defined after useLoungeWebRTC hook
  
  // Internal leave function (performs actual cleanup - called after reflection)
  const performLeave = useCallback(async () => {
    try {
      // Stop local video/audio before leaving
      if (typeof turnCameraOff === 'function') {
        turnCameraOff();
      }
      
      // Clear participants list
      setParticipants([]);
      
      // Call centralized leave (handles backend + state)
      await sessionLeaveLounge();
      
      // 🔄 Refresh Unity Flow UI so green circles update immediately
      if (window.refreshStreakAndBadges) {
        window.refreshStreakAndBadges();
      }
    } catch (error) {
      console.error('[LifestyleLounge] Error in performLeave:', error);
    }
  }, [sessionLeaveLounge]);

  // User-facing leave function - shows reflection modal first (if not already shown)
  const leaveLounge = async () => {
    // If reflection modal already shown this session, leave immediately
    if (hasShownReflectionRef.current) {
      await performLeave();
      return;
    }
    
    // Mark pending leave and show reflection modal
    pendingLeaveRef.current = true;
    hasShownReflectionRef.current = true;
    setShowReflectionModal(true);
  };
  
  // Handle reflection modal close (Save or Skip)
  const handleReflectionClose = useCallback(async () => {
    setShowReflectionModal(false);
    
    // If we were in a leave flow, complete it now
    if (pendingLeaveRef.current) {
      pendingLeaveRef.current = false;
      await performLeave();
    }
  }, [performLeave]);

  // Phase 4: Graceful out-of-time disconnect
  useEffect(() => {
    if (inLounge && isOutOfTime) {
      console.log('[Phase 4] Out of time detected - showing toast and preparing to leave');
      
      // Show toast notification
      toast.info(
        "You're out of Lounge time for today. We'll close the Lounge in a few seconds.",
        {
          duration: 4000,
          position: 'top-center',
        }
      );

      // Auto-leave after 5 seconds
      const timer = setTimeout(async () => {
        console.log('[Phase 4] Auto-leaving due to out of time');
        await leaveLounge();
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [inLounge, isOutOfTime, leaveLounge]);

  // WebRTC state (legacy - keeping for backward compatibility with old WebRTC code)
  // const [remoteStreams, setRemoteStreams] = useState({}); // Moved to useLoungeWebRTC hook
  const peerConnectionsRef = useRef({}); // { userId: RTCPeerConnection }
  
  // 🔄 Refresh Unity Flow on mount so badges/circles are fresh
  useEffect(() => {
    if (window.refreshStreakAndBadges) {
      window.refreshStreakAndBadges();
    }
  }, []);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  // Time warning & paywall state
  const [showLowTimeToast, setShowLowTimeToast] = useState(false);
  const [showOutOfTimeModal, setShowOutOfTimeModal] = useState(false);
  const [isPurchasingBoost, setIsPurchasingBoost] = useState(false);

  // Ticket 14: Moderation state
  const [showPartnerMenu, setShowPartnerMenu] = useState(false);
  const [showLoungeReportModal, setShowLoungeReportModal] = useState(false);
  const [loungeReportReason, setLoungeReportReason] = useState('');

  // Ticket 27: Lounge gifting state
  const [giftToast, setGiftToast] = useState(null);
  const { triggerGiftAnimation } = useGiftAnimations();

  // Request Chat V1: Chat request system
  const chatRequests = useLoungeChatRequests({
    token,
    enabled: inLounge
  });


  // Get Ably instance for WebRTC
  const [ablyInstance, setAblyInstance] = useState(null);
  
  useEffect(() => {
    getRealtimeClient().then(ably => setAblyInstance(ably));
  }, []);

  // Handle boost purchase success/cancel redirects
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const boostStatus = urlParams.get('boost');
    
    if (boostStatus === 'success') {
      // Show success message
      alert('✨ Boost purchased successfully! Your extra video time has been added.');
      
      // Refresh status to show new boost time
      if (loungeStatus) {
        // loadLoungeStatus is defined later in the component - will be called on re-render
        window.location.reload();  // Simple fix: reload to refresh status
      }
      
      // Clean up URL
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
    } else if (boostStatus === 'cancel') {
      // User cancelled payment
      console.log('Boost purchase cancelled by user');
      
      // Clean up URL
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
    }
  }, []);

  // New WebRTC hook with proper pairing
  const {
    participants: webrtcParticipants,
    localStream,
    remoteStream,
    status: webrtcStatus,
    currentPartnerId,
    isPolite,
    turnCameraOn,
    turnCameraOff,
    skipPartner,
    connectionHealth, // Ticket 13: WebRTC health monitoring
  } = useLoungeWebRTC({
    ably: ablyInstance,
    userId: currentUserId,
    displayName: user?.first_name || user?.display_name || user?.name || 'Guest',
    roomKey: activeRoomKey, // Ticket 22: Pass active room
    sex: user?.gender,
  });

  // Camera state derived from WebRTC hook
  const isCameraOn = !!localStream;
  const isCameraRequesting = webrtcStatus === 'connecting';

  // 🎯 PATCH: Auto-exit fullscreen when camera turns off (safety - never trap user)
  // MOVED HERE after isCameraOn is defined to prevent render-time reference error
  useEffect(() => {
    if (isVideoFullscreen && !isCameraOn) {
      setIsVideoFullscreen(false);
    }
  }, [isVideoFullscreen, isCameraOn]);

  // Camera control handlers (MOVED HERE after hook declaration)
  const handleTurnCameraOn = async () => {
    try {
      const stream = await turnCameraOn();
      // Inline attach + play (Android Chrome user-gesture chain)
      const el = localVideoRef.current;
      if (el && stream) {
        el.srcObject = stream;
        el.muted = true;
        el.autoplay = true;
        el.playsInline = true;
        el.setAttribute("playsinline", "");
        el.setAttribute("webkit-playsinline", "");
        el.setAttribute("autoplay", "");
        try {
          const p = el.play();
          if (p && typeof p.then === "function") await p;
        } catch (e) {
          console.warn('[lounge] inline play failed; relying on retries', e);
        }
      }
    } catch (err) {
      console.error('[lounge] Failed to turn camera on:', err);
    }
  };

  const handleTurnCameraOff = () => {
    turnCameraOff();
  };

  // Local video ref for rendering
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);

  // Sync local stream to video element (with Android-Chrome-friendly retries)
  useEffect(() => {
    const el = localVideoRef.current;
    if (!el || !localStream) return;

    el.srcObject = localStream;
    el.muted = true;
    el.autoplay = true;
    el.playsInline = true;
    el.setAttribute("playsinline", "");
    el.setAttribute("webkit-playsinline", "");
    el.setAttribute("autoplay", "");

    const tryPlay = () => {
      try { el.play()?.catch((e) => console.warn('[lounge] local play blocked', e)); } catch {}
    };
    const onLoaded = () => tryPlay();
    el.addEventListener("loadedmetadata", onLoaded);
    requestAnimationFrame(tryPlay);
    setTimeout(tryPlay, 250);
    setTimeout(tryPlay, 800);

    return () => {
      el.removeEventListener("loadedmetadata", onLoaded);
    };
  }, [localStream]);

  // Sync remote stream to video element (same retry pattern)
  useEffect(() => {
    const el = remoteVideoRef.current;
    if (!el || !remoteStream) return;

    el.srcObject = remoteStream;
    el.autoplay = true;
    el.playsInline = true;
    el.setAttribute("playsinline", "");
    el.setAttribute("webkit-playsinline", "");
    el.setAttribute("autoplay", "");

    const tryPlay = () => {
      try { el.play()?.catch((e) => console.warn('[lounge] remote play blocked', e)); } catch {}
    };
    const onLoaded = () => tryPlay();
    el.addEventListener("loadedmetadata", onLoaded);
    requestAnimationFrame(tryPlay);
    setTimeout(tryPlay, 250);

    return () => {
      el.removeEventListener("loadedmetadata", onLoaded);
    };
  }, [remoteStream]);

  // Phase 2: Lounge-wide reactions (bubbles that rise and fade)
  const { activeReactions, sendReaction, reactionTypes } = useLoungeReactions({
    loungeId: activeRoomKey,
    userId: currentUserId,
    ably: ablyInstance,
    registerCleanup,
  });

  // Phase 3: Time & Streak awareness badges (REMOVED - moved up to fix hoisting issue)

  // Update participants state from WebRTC hook - MERGE with existing backend data
  useEffect(() => {
    if (webrtcParticipants && webrtcParticipants.length > 0) {
      setParticipants(prev => {
        // Build a map of existing backend-enriched participants
        const backendMap = {};
        for (const p of prev) {
          if (p.user_id) backendMap[p.user_id] = p;
        }
        // Merge WebRTC presence into existing backend data
        return webrtcParticipants.map(p => {
          const existing = backendMap[p.clientId] || {};
          return {
            ...existing,
            user_id: p.clientId,
            first_name: existing.first_name || p.displayName,
            display_name: existing.display_name || p.displayName,
            sex: p.sex,
            // Preserve backend profile data if it exists
            profile: existing.profile || null,
            avatar_url: existing.avatar_url || null,
          };
        });
      });
    }
  }, [webrtcParticipants]);

  // Phase 1: Register WebRTC cleanup with session
  useEffect(() => {
    if (!inLounge) return;
    
    // Register cleanup function for WebRTC resources
    const unregister = registerCleanup(() => {
      console.log('[LoungeSession] Cleaning up WebRTC');
      turnCameraOff();
    });
    
    return unregister;
  }, [inLounge, registerCleanup, turnCameraOff]);

  // Camera control handlers (MOVED UP to fix hoisting issue)

  const handleSkipPartner = () => {
    if (skipPartner) {
      skipPartner();
    }
  };

  // Ticket 22: Room-specific match ID (replaces single LOUNGE_MATCH_ID)
  // const LOUNGE_MATCH_ID = 'lounge:lifestyle:main'; // OLD - replaced by loungeMatchId

  // Lounge Chat Reaction Handler - Uses REST API (works even when Ably is disabled)
  const toggleReaction = useCallback(async (messageId, emoji) => {
    console.log(`[LoungeReaction] Toggling ${emoji} on message ${messageId}`);

    try {
      // Use centralized API client for consistent auth
      const response = await api.post('/lounge/chat/react', {
        message_id: messageId,
        emoji: emoji
      });

      const result = response.data;
      console.log(`[LoungeReaction] ✅ ${result.action} ${emoji} on ${messageId}`);

      // Update local state with the new reactions
      // Backend returns: { "❤️": ["userId1", "userId2"], ... }
      setMessages(prev => prev.map(m => 
        m.id === messageId ? { ...m, reactions: result.reactions } : m
      ));
    } catch (error) {
      console.error('[LoungeReaction] Error:', error);
      toast.error('Failed to add reaction');
    }
  }, []);

  // Ticket 9: Helper functions for reactions (Option B structure)
  const getReactionSummary = (message) => {
    // reactions is now: { "❤️": ["user1", "user2"], "🔥": ["user3"] }
    const reactions = message.reactions || {};
    return Object.entries(reactions).map(([emoji, userIds]) => ({
      emoji,
      count: userIds.length,
    })).filter(r => r.count > 0); // Only show reactions with counts
  };

  const userHasReaction = (message, emoji, userId) => {
    // Check if userId is in the array for this emoji
    const reactions = message.reactions || {};
    const userIds = reactions[emoji] || [];
    return userIds.includes(userId);
  };

  // Ticket 7: Handle scroll to detect if user is at bottom
  const handleChatScroll = (e) => {
    const el = e.currentTarget;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    
    // Consider "at bottom" if within 10px of the actual bottom
    const atBottom = distanceFromBottom < 10;
    setIsAtBottom(atBottom);
    
    // If user manually scrolls back to bottom, clear the "new messages" indicator
    if (atBottom) {
      setHasNewMessages(false);
    }
  };

  // Ticket 7: Scroll to bottom when user clicks the "new messages" pill
  const handleScrollToBottom = () => {
    if (!messagesContainerRef.current) return;
    // Use scrollTop instead of scrollIntoView to avoid scrolling the entire page
    messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    setHasNewMessages(false);
    setIsAtBottom(true);
  };

  // Ticket 11: Audio recorder for voice messages in lounge
  const {
    isRecording: isRecordingAudio,
    durationMs: audioDurationMs,
    error: audioError,
    startRecording: startAudioRecording,
    stopRecording: stopAudioRecording,
    cancelRecording: cancelAudioRecording,
  } = useAudioRecorder();

  const handleSendAudioMessage = async () => {
    try {
      const result = await stopAudioRecording();
      if (!result) return; // too short or cancelled

      const { blob, durationMs } = result;

      const formData = new FormData();
      formData.append('file', blob, 'lounge-voice-message.webm');
      formData.append('duration_ms', String(durationMs));

      // Ticket 22: Use room-specific match ID
      await fetch(`/chat/${loungeMatchId}/messages/audio`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      // Audio message will arrive via Ably subscription
    } catch (err) {
      console.error('[lounge] Failed to send audio message', err);
    }
  };

  const handleMicClick = async () => {
    if (!isRecordingAudio) {
      try {
        await startAudioRecording();
      } catch (err) {
        console.error('[lounge] Failed to start recording', err);
      }
    } else {
      await handleSendAudioMessage();
    }
  };

  const handleCancelRecording = () => {
    cancelAudioRecording();
  };

  // Mark step 3: "Visit the lifestyle lounge"
  useEffect(() => {
    markFlowStep('joinedLounge');
  }, []);

  // Fetch lounge status on mount and auto-rejoin if session active
  useEffect(() => {
    if (!token) return;

    const fetchStatus = async () => {
      try {
        setIsLoadingStatus(true);
        const res = await apiClient.get(`/lounge/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const status = res.data;
        setLoungeStatus(status);
        // setInLounge removed - now managed by useLoungeSession hook
        const rs =
          typeof status?.remaining_seconds_today === "number"
            ? status.remaining_seconds_today
            : (typeof status?.remaining_seconds === "number" ? status.remaining_seconds : null);
        setRemainingSeconds(rs);
      } catch (err) {
        console.error("Failed to fetch lounge status", err);
      } finally {
        setIsLoadingStatus(false);
        setLoading(false);
      }
    };

    fetchStatus();
  }, [token]);
  
  // Refs
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const timerIntervalRef = useRef(null);

  // Lounge join key for persistence
  const LOUNGE_JOIN_KEY = "unitylove_lounge_joined_v1";

  // Get current user ID (handle both id and _id) - keeping only one definition
  // const currentUserId = user?.id || user?._id; // Already defined above for WebRTC hook


  // API Helpers for Lounge Presence
  const apiJoinLounge = async () => {
    try {
      const res = await apiClient.post(`/lounge/join`, {
        avatar_url: null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 200) {
        // setInLounge removed - now managed by useLoungeSession hook
        localStorage.setItem(LOUNGE_JOIN_KEY, "true");
        console.log('✅ Joined lounge successfully');
        startLocalMedia(); // Start local media after joining
      }
    } catch (error) {
      console.error('Failed to join lounge:', error);
    }
  };

  const apiLeaveLounge = async () => {
    try {
      await apiClient.post(`/lounge/leave`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // setInLounge removed - now managed by useLoungeSession hook
      localStorage.removeItem(LOUNGE_JOIN_KEY);
      console.log('✅ Left lounge successfully');
    } catch (error) {
      console.error('Failed to leave lounge:', error);
    }
  };

  const fetchParticipants = async () => {
    try {
      const res = await apiClient.get(`/lounge/participants`, {
        params: { room_key: activeRoomKey },
        headers: { Authorization: `Bearer ${token}` }
      });
      setParticipants(res.data);
      console.log(`✅ Loaded ${res.data.length} participants for room=${activeRoomKey}`);
    } catch (error) {
      console.error('Failed to fetch participants:', error);
    }
  };

  const startLocalMedia = async () => {
    // Use new WebRTC hook camera control
    await handleTurnCameraOn();
  };


  // Format time remaining
  const formatTime = (seconds) => {
    if (seconds === -1 || seconds === Infinity) return '∞ Max Time';
    return formatRemainingTime(seconds);
  };

  // Format time for short display
  const formatTimeShort = (seconds) => {
    if (seconds == null) return "--";
    if (seconds <= 0) return "0m";

    const mins = Math.floor(seconds / 60);
    const hrs = Math.floor(mins / 60);
    const remMins = mins % 60;

    if (hrs > 0) return `${hrs}h ${remMins}m`;
    return `${mins}m`;
  };

  // Load lounge status
  useEffect(() => {
    loadLoungeStatus();
  }, []);

  // Ticket 7: Smart auto-scroll - only scroll if user is at bottom
  useEffect(() => {
    if (!messagesContainerRef.current) return;

    if (isAtBottom) {
      // User is at bottom - auto-scroll to show new messages
      // Use scrollTop instead of scrollIntoView to keep scroll contained to chat
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    } else {
      // User is reading old messages - set the "new messages" indicator
      if (messages.length > 0) {
        setHasNewMessages(true);
      }
    }
  }, [messages.length, isAtBottom]);

  // Ably presence for WebRTC is now handled by useLoungeWebRTC hook
  // (removed duplicate presence setup)

  // Real-time updates via Ably (primary)
  useEffect(() => {
    if (!inLounge) {
      console.log('🏖️ Lounge: Not in lounge, skipping Ably subscription');
      return;
    }
    
    // Ticket 22: Use active room key instead of hardcoded 'main'
    const channelName = `lounge:${activeRoomKey}`;
    console.log(`🏖️ Lounge: Setting up Ably subscription for room="${activeRoomKey}"`);
    
    const unsub = subscribeToEvent(channelName, 'message-created', (payload) => {
      console.log(`🏖️ Lounge: Received message from Ably (room=${activeRoomKey}):`, payload);
      
      setMessages(prev => {
        const incoming = normalizeLoungeMessage(payload);
        console.log(`🏖️ Lounge: Normalized message:`, incoming);

        // Check if message already exists
        const idx = prev.findIndex(
          m => m.id === incoming.id && m.text === incoming.text
        );

        if (idx !== -1) {
          const existing = prev[idx];
          const existingIsUuid = UUID_REGEX.test(
            existing.displayName || existing.rawName || ""
          );
          const incomingHasGoodName = !!incoming.displayName;

          // If existing has UUID name but incoming has real name, upgrade it
          if (existingIsUuid && incomingHasGoodName) {
            console.log(`🔄 Lounge: Upgrading message name from UUID to real name`);
            const merged = {
              ...existing,
              displayName: incoming.displayName,
              rawName: incoming.rawName,
            };
            const copy = [...prev];
            copy[idx] = merged;
            return copy;
          }

          console.log(`⚠️ Lounge: Duplicate message detected, skipping`);
          return prev;
        }

        console.log(`✅ Lounge: Adding new message to state`);
        return [...prev, incoming];
      });
    });
    
    return () => {
      console.log(`🏖️ Lounge: Cleaning up Ably subscription for room="${activeRoomKey}"`);
      unsub();
    };
  }, [inLounge, activeRoomKey]); // Ticket 22: Added activeRoomKey to deps for room switching


  // Ticket 27: Listen for gift events in lounge
  useEffect(() => {
    if (!inLounge || !ablyInstance) {
      return;
    }
    
    const channelName = `lounge:${activeRoomKey}`;
    console.log(`🎁 Lounge: Setting up gift event subscription for room="${activeRoomKey}"`);
    
    const unsubGift = subscribeToEvent(channelName, 'gift', (payload) => {
      console.log(`🎁 Lounge: Received gift event (room=${activeRoomKey}):`, payload);
      
      if (payload.type !== 'gift') return;
      
      // Only show animations/toasts for the RECEIVER (not the sender)
      // Sender already gets feedback from GiftPickerModal
      if (user && payload.to_user_id === user.id) {
        console.log(`🎁 I'm the receiver! Showing gift from ${payload.from_user_name}`);
        
        // 1. Trigger full-screen animation for receiver
        triggerGiftAnimation({
          gift_key: payload.gift_key,
          gift_name: payload.gift_name,
          gift_emoji: payload.gift_emoji,
          animation: payload.gift_key,  // Use gift key as animation type
          fromUserName: payload.from_user_name,
          toUserName: payload.to_user_name || 'you',
        });
        
        // 2. Show toast banner for receiver
        setGiftToast({
          giftEmoji: payload.gift_emoji,
          giftName: payload.gift_name,
          fromUserName: payload.from_user_name,
          toUserName: payload.to_user_name || 'you',
        });
      } else {
        // Optional: log that this gift is for someone else
        console.log(`🎁 Gift is for ${payload.to_user_name} (not me)`);
      }
    });
    
    return () => {
      console.log(`🎁 Lounge: Cleaning up gift subscription for room="${activeRoomKey}"`);
      unsubGift();
    };
  }, [inLounge, activeRoomKey, ablyInstance, triggerGiftAnimation]);


  // Auto-rejoin lounge after refresh
  useEffect(() => {
    if (!token) return;

    // Fetch initial participants from backend (for UI display)
    fetchParticipants();

    // Auto rejoin after refresh
    if (localStorage.getItem(LOUNGE_JOIN_KEY) === "true") {
      console.log('🔄 Auto-rejoining lounge after refresh');
      apiJoinLounge();
    }

    return () => {
      // Leave lounge on unmount
      apiLeaveLounge();
    };
  }, [token]);

  // Phase 2: WebRTC signaling is now handled by useLoungeWebRTC hook
  // Legacy manual WebRTC code removed


  // Real-time updates via SSE (fallback) - MEMOIZED to prevent re-render loop
  const sseMessageHandler = useCallback((evt) => {
    switch (evt.type) {
      case 'message:new':
        setMessages(prev => [...prev, {
          id: `m-${Date.now()}-${Math.random()}`,
          user_id: evt.payload.from,
          first_name: evt.payload.from,
          content: evt.payload.content,
          timestamp: new Date(evt.payload.timestamp * 1000)
        }]);
        break;
      case 'participant:joined':
        console.log('[lounge] Participant joined:', evt.payload);
        setParticipants(prev => {
          if (prev.some(p => p.user_id === evt.payload.user_id)) return prev;
          const newParticipant = {
            user_id: evt.payload.user_id,
            first_name: evt.payload.first_name || evt.payload.display_name || 'Member',
            photo_url: evt.payload.photo_url,
            is_video_on: evt.payload.is_video_on !== undefined ? evt.payload.is_video_on : true,
            is_audio_on: evt.payload.is_audio_on !== undefined ? evt.payload.is_audio_on : true,
          };
          return [...prev, newParticipant];
        });
        break;
      case 'participant:left':
        console.log('[lounge] Participant left:', evt.payload);
        setParticipants(prev => prev.filter(p => p.user_id !== evt.payload.user_id));
        break;
      case 'typing:start':
        setTypingUsers(prev => ({ ...prev, [evt.payload.user_id]: Date.now() }));
        break;
      case 'typing:stop':
        setTypingUsers(prev => {
          const next = { ...prev };
          delete next[evt.payload.user_id];
          return next;
        });
        break;
      default:
        break;
    }
  }, []);

  const sseUrl = useMemo(() => {
    return token && inLounge ? () => `/lounge/events` : null;
  }, [token, inLounge]);

  useSSE(sseUrl, { onMessage: sseMessageHandler });

  // Expire stale typing indicators (3 seconds)
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setTypingUsers(prev => {
        const next = { ...prev };
        for (const userId of Object.keys(next)) {
          if (now - next[userId] > 3000) delete next[userId];
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Ticket 22: Reload messages when room changes
  useEffect(() => {
    if (inLounge) {
      setMessages([]); // Clear old messages
      loadMessages(); // Load messages for new room
    }
  }, [activeRoomKey, inLounge]);

  // Reliable polling for participants/messages while in lounge
  useEffect(() => {
    if (!inLounge || !token) return;

    // Immediate load on join
    loadParticipants();
    loadMessages();

    // Poll participants every 3s, messages every 5s
    const participantInterval = setInterval(() => {
      loadParticipants();
    }, 3000);
    const messageInterval = setInterval(() => {
      loadMessages();
    }, 5000);

    return () => {
      clearInterval(participantInterval);
      clearInterval(messageInterval);
    };
  }, [inLounge, token, activeRoomKey]);

  // Timer countdown - now using remainingSeconds (handled by countdown effect above)

  // Warn before leaving/refreshing while in lounge
  useEffect(() => {
    if (!inLounge) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = ''; // Modern browsers require this to show the dialog
      return ''; // Legacy browsers
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [inLounge]);

  // Countdown effect based on remainingSeconds
  useEffect(() => {
    if (!inLounge) return;
    if (remainingSeconds == null) return;

    if (remainingSeconds <= 0) {
      console.log('[lounge] ⏰ Time expired - auto-kicking user');
      setRemainingSeconds(0);
      setShowLowTimeToast(false);
      setShowOutOfTimeModal(true);
      // Auto-kick: properly leave the lounge
      leaveLounge();
      return;
    }

    const id = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev == null) return prev;
        const next = prev - 1;

        // Show low-time toast at 5 min left
        if (next <= 300 && next > 0) {
          setShowLowTimeToast(true);
        }
        if (next <= 0) {
          console.log('[lounge] ⏰ Countdown reached 0 - auto-kicking user');
          setShowLowTimeToast(false);
          setShowOutOfTimeModal(true);
          // Auto-kick: properly leave the lounge
          leaveLounge();
          return 0;
        }
        return next;
      });
    }, 1000);

    return () => clearInterval(id);
  }, [inLounge, remainingSeconds]);

  const loadLoungeStatus = async () => {
    try {
      const response = await apiClient.get(`/lounge/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLoungeStatus(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load lounge status:', error);
      setError('Failed to load lounge status');
      setLoading(false);
    }
  };

  const loadParticipants = async () => {
    try {
      const response = await apiClient.get(`/lounge/participants`, {
        params: { room_key: activeRoomKey },
        headers: { Authorization: `Bearer ${token}` }
      });
      setParticipants(response.data);
    } catch (error) {
      console.error('Failed to load participants:', error);
    }
  };

  const loadMessages = async () => {
    try {
      // Use centralized API client for consistent auth
      const response = await api.get('/lounge/chat/recent', {
        params: { room_key: activeRoomKey, limit: 50 }
      });
      // Normalize all initial messages
      const normalized = response.data.map(normalizeLoungeMessage);
      setMessages(normalized);
      console.log(`[CHAT] Loaded ${normalized.length} messages for room=${activeRoomKey}`);
    } catch (error) {
      console.error('Failed to load messages:', error);
      // Don't crash - just start with empty messages
      setMessages([]);
    }
  };

  const initializeMedia = async () => {
    try {
      // Use new WebRTC hook camera control
      console.warn('getMedia() called - use handleTurnCameraOn() instead');
      return null;
    } catch (error) {
      console.error('Error accessing media devices:', error);
      setError('Could not access camera/microphone. You can still participate via text chat.');
      return null;
    }
  };

  // Phase 1: Wrap session joinLounge with loading state management
  const joinLounge = async () => {
    setLoading(true);
    await sessionJoinLounge();
    // Load participants and messages after successful join
    // (can't do this in callback due to function hoisting issues)
    await loadParticipants();
    await loadMessages();
    setLoading(false);
  };

  // Phase 1: Use centralized leave with cleanup (REMOVED - moved up to fix hoisting issue)

  const toggleVideo = () => {
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoOn(videoTrack.enabled);
        
        // Update status on server
        updateMediaStatus(!isVideoOn, isAudioOn);
      }
    }
  };

  const toggleAudio = () => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsAudioOn(audioTrack.enabled);
        
        // Update status on server
        updateMediaStatus(isVideoOn, !isAudioOn);
      }
    }
  };

  const updateMediaStatus = async (video, audio) => {
    try {
      const formData = new FormData();
      formData.append('is_video_on', video);
      formData.append('is_audio_on', audio);

      await apiClient.post(`/lounge/update-media-status`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Failed to update media status:', error);
    }
  };

  // Sending state to prevent double-tap
  const [isSending, setIsSending] = useState(false);

  const sendMessage = async (e) => {
    // Handle both form submit and direct button click
    if (e) e.preventDefault();
    
    const content = messageInput.trim();
    if (!content || isSending) {
      console.log('[CHAT] Skip send: empty or already sending');
      return;
    }

    console.log(`🏖️ [CHAT] Sending message to room="${activeRoomKey}":`, content);
    setIsSending(true);

    try {
      // Use centralized API client for consistent auth handling
      const response = await api.post('/lounge/chat/send', {
        message: content,
        room_key: activeRoomKey
      });

      const sentMessage = response.data;
      console.log('[CHAT] ✅ Message sent via REST:', sentMessage.id);

      // Clear input FIRST (before state update to prevent race)
      setMessageInput('');
      
      // Optimistically add to local messages
      setMessages(prev => [...prev, sentMessage]);

    } catch (error) {
      console.error('❌ [CHAT] Failed to send message:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to send message';
      toast.error(errorMsg);
    } finally {
      setIsSending(false);
    }
  };

  // Throttled typing indicator
  const sendTypingIndicator = useMemo(() => {
    let lastSent = 0;
    return async () => {
      const now = Date.now();
      if (now - lastSent < 2000) return; // Throttle to every 2 seconds
      lastSent = now;
      try {
        // Ticket 22: Send typing to active room
        await apiClient.post(`/lounge/typing`, {
          room: activeRoomKey,
          state: 'start'
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Failed to send typing indicator:', error);
      }
    };
  }, [token]);

  const voteToBootUser = async (targetUserId) => {
    if (window.confirm('Vote to remove this person from the lounge? This helps keep conversations comfortable.')) {
      try {
        const response = await apiClient.post(`/lounge/boot-vote/${targetUserId}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.booted) {
          alert('User has been booted from the lounge!');
        } else {
          alert(`Boot vote registered (${response.data.boot_percentage}%)`);
        }

        await loadParticipants();
      } catch (error) {
        alert(error.response?.data?.detail || 'Failed to vote');
      }
    }
  };

  const toggleMuteUser = (userId) => {
    setMutedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const toggleHideVideo = (userId) => {
    setHiddenVideos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const upgradeSubscription = async (tier) => {
    try {
      const formData = new FormData();
      formData.append('tier', tier);

      const response = await apiClient.post(`/lounge/subscribe`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert(`Successfully upgraded! ${response.data.message}`);
      setShowUpgradeModal(false);
      await loadLoungeStatus();
    } catch (error) {
      alert('Failed to upgrade subscription');
    }
  };

  // Handle boost purchase from paywall modal
  // FIX: Use { product } schema to match backend expectation
  const handleBuyBoost = async (hours) => {
    const boostType = hours === 1 ? 'boost_1h' : 'boost_4h';
    
    try {
      setIsPurchasingBoost(true);
      const response = await apiClient.post(
        `/lounge/boost`,
        { product: boostType },  // FIX: Changed from boost_type to product
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          } 
        }
      );
      
      // Redirect to Stripe Checkout if URL returned
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
        return;
      }
      
      // Reload lounge status to get updated time
      await loadLoungeStatus();
      
      // Close the modal
      setShowOutOfTimeModal(false);
      
      // Show success message
      alert(`✨ Boost purchased! You now have ${response.data.boost_seconds_available_human} of extra video time.`);
    } catch (error) {
      console.error('Failed to purchase boost:', error);
      const errorMsg = error.response?.data?.detail || 'Unable to purchase boost right now. Please try again.';
      alert(errorMsg);
    } finally {
      setIsPurchasingBoost(false);
    }
  };

  // Navigate to premium page
  const handleGoPremium = () => {
    navigate('/premium');
  };

  // Navigate to premium page (elite section)
  const handleGoElite = () => {
    navigate('/premium#elite');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-warm">
        <div className="text-2xl font-playfair text-rose-600">Loading...</div>
      </div>
    );
  }

  // Blocked screen
  if (loungeStatus && loungeStatus.is_blocked) {
    return (
      <div className="min-h-screen bg-gradient-warm py-8">
        <div className="max-w-2xl mx-auto px-4">
          <button
            type="button"
            onClick={() => {
              // FIX: navigate(-1) with fallback route
              if (window.history.length > 1) {
                navigate(-1);
              } else {
                navigate('/dashboard');
              }
            }}
            className="relative z-20 flex items-center space-x-2 text-gray-700 hover:text-rose-600 mb-6 py-4 px-2 -ml-2 touch-manipulation min-h-[44px]"
            data-testid="lounge-back-dashboard-btn"
            aria-label="Back to Dashboard"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Dashboard</span>
          </button>

          <div className="glass rounded-2xl p-8 text-center">
            <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="font-playfair text-3xl font-bold text-gray-900 mb-4">
              Temporarily Blocked
            </h2>
            <p className="text-gray-600 mb-4">
              You've been temporarily blocked from the Lifestyle Lounge due to community reports.
            </p>
            <p className="text-lg font-semibold text-rose-600">
              Block expires: {new Date(loungeStatus.blocked_until).toLocaleString()}
            </p>
            <p className="text-sm text-gray-500 mt-4">
              Please review our Community Guidelines to ensure a positive experience for everyone.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Entrance screen
  if (!inLounge) {
    return (
      <LoungeBackground>
        <div className="min-h-screen py-8">
        <div className="max-w-4xl mx-auto px-4">
          <button
            type="button"
            onClick={() => {
              // FIX: navigate(-1) with fallback route
              if (window.history.length > 1) {
                navigate(-1);
              } else {
                navigate('/dashboard');
              }
            }}
            className="relative z-20 flex items-center space-x-2 text-gray-700 hover:text-rose-600 mb-6 py-4 px-2 -ml-2 touch-manipulation min-h-[44px]"
            data-testid="lounge-back-dashboard-btn"
            aria-label="Back to Dashboard"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Dashboard</span>
          </button>

          <div className="glass rounded-2xl p-8">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-gradient-rose-gold rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-10 h-10 text-white" />
              </div>
              <h1 className="font-playfair text-4xl font-bold text-gray-900 mb-2">
                The Lifestyle Lounge
              </h1>
              <p className="text-xl text-gray-600">
                A virtual space to connect, chat, and hangout with the community
              </p>
            </div>

            {/* Time remaining */}
            <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-6 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Clock className="w-6 h-6 text-purple-600" />
                  <div>
                    <div className="font-semibold text-gray-900">Time Remaining Today</div>
                    <div className="text-2xl font-bold text-purple-600">
                      {formatTime(loungeRemainingSeconds !== null ? loungeRemainingSeconds : (remainingSeconds !== null ? remainingSeconds : 0))}
                    </div>
                  </div>
                </div>
                {loungeStatus?.subscription === 'free' && (
                  <button
                    onClick={() => setShowUpgradeModal(true)}
                    className="btn-primary flex items-center space-x-2"
                  >
                    <Crown className="w-5 h-5" />
                    <span>Upgrade</span>
                  </button>
                )}
              </div>
            </div>

            {/* Video Boost Panel - One-time purchases for extra time */}
            {loungeStatus?.subscription !== 'lounge_unlimited' && (
              <VideoBoostPanel token={token} />
            )}

            {/* Features */}
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <div className="bg-white/50 rounded-xl p-4 text-center">
                <Video className="w-8 h-8 text-rose-600 mx-auto mb-2" />
                <div className="font-semibold text-gray-900">Video & Audio</div>
                <div className="text-sm text-gray-600">See and hear everyone</div>
              </div>
              <div className="bg-white/50 rounded-xl p-4 text-center">
                <Send className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <div className="font-semibold text-gray-900">Text Chat</div>
                <div className="text-sm text-gray-600">Message the group</div>
              </div>
              <div className="bg-white/50 rounded-xl p-4 text-center">
                <Shield className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <div className="font-semibold text-gray-900">Safe Space</div>
                <div className="text-sm text-gray-600">Community-moderated</div>
              </div>
            </div>

            {/* Safety info */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div className="flex-1">
                  <div className="font-semibold text-yellow-900 mb-1">Community Guidelines</div>
                  <div className="text-sm text-yellow-800">
                    Be respectful and authentic. If 60% of participants vote to boot someone, they'll be blocked for 72 hours. 
                    Individual mute controls available for your comfort.
                  </div>
                </div>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6">
                {error}
              </div>
            )}

            <div className="text-center">
              <button
                onClick={(loungeRemainingSeconds !== null ? loungeRemainingSeconds : remainingSeconds) <= 0 && (loungeRemainingSeconds !== null || remainingSeconds !== null) ? () => setShowUpgradeModal(true) : joinLounge}
                className="btn-primary px-12 py-4 text-lg flex items-center justify-center gap-2 mx-auto"
              >
                {(loungeRemainingSeconds !== null ? loungeRemainingSeconds : remainingSeconds) <= 0 && (loungeRemainingSeconds !== null || remainingSeconds !== null) ? (
                  <>
                    <Crown className="w-5 h-5" />
                    <span>Add More Time to Enter</span>
                  </>
                ) : (
                  'Enter the Lounge'
                )}
              </button>
              {(loungeRemainingSeconds !== null ? loungeRemainingSeconds : remainingSeconds) <= 0 && (loungeRemainingSeconds !== null || remainingSeconds !== null) && (
                <p className="text-sm text-slate-400 mt-2">
                  You're out of time today. Add a boost or upgrade to join.
                </p>
              )}
            </div>
          </div>

          {/* ---- Improved 3-Tier Card Layout (Premium Mobile UX) ---- */}
          <section className="mt-8 w-full px-4 pt-8 pb-10 bg-slate-950/95 rounded-3xl">
            <p className="text-center text-sm font-semibold text-slate-400 uppercase tracking-wide mb-6">Membership Options</p>
            <div className="max-w-5xl mx-auto space-y-4 md:space-y-0 md:grid md:grid-cols-3 md:gap-6">
              {/* Free Tier */}
              <div className="rounded-3xl bg-slate-900/90 px-5 py-4 border border-slate-800">
                <p className="text-base md:text-lg font-semibold text-slate-300">Free</p>
                <p className="text-2xl md:text-3xl font-bold text-pink-400">$0</p>
                <p className="mt-1 text-sm text-slate-300 leading-snug">20 minutes daily</p>
                <p className="text-sm text-slate-500 leading-snug">Perfect for trying out</p>
              </div>

              {/* Inner Circle - Featured */}
              <div className="rounded-3xl bg-slate-900/95 px-5 py-4 border border-purple-500/60 shadow-[0_0_25px_rgba(168,85,247,0.35)]">
                <p className="text-base md:text-lg font-semibold text-slate-50">Inner Circle</p>
                <p className="text-2xl md:text-3xl font-bold text-purple-400">$14.99/mo</p>
                <p className="mt-1 text-sm text-slate-300 leading-snug">40 min daily</p>
                <p className="text-sm text-slate-500 leading-snug">Priority discovery</p>
              </div>

              {/* Elite */}
              <div className="rounded-3xl bg-slate-900/90 px-5 py-4 border border-amber-500/60">
                <p className="text-base md:text-lg font-semibold text-slate-50">Elite</p>
                <p className="text-2xl md:text-3xl font-bold text-amber-400">$19.99/mo</p>
                <p className="mt-1 text-sm text-slate-300 leading-snug">1 hour daily</p>
                <p className="text-sm text-slate-500 leading-snug">Most popular</p>
              </div>

              {/* Elite+ */}
              <div className="rounded-3xl bg-slate-900/90 px-5 py-4 border border-pink-500/60">
                <p className="text-base md:text-lg font-semibold text-slate-50">Elite+</p>
                <p className="text-2xl md:text-3xl font-bold text-pink-400">$39.99/mo</p>
                <p className="mt-1 text-sm text-slate-300 leading-snug">2 hours daily</p>
                <p className="text-sm text-slate-500 leading-snug">VIP experience</p>
              </div>
            </div>

            {/* Unity Love Footer */}
            <footer className="mt-8 border-t border-slate-800/60 pt-4 pb-2 max-w-5xl mx-auto">
              <p className="text-sm font-semibold text-slate-100">❤️ Unity Love</p>
              <p className="mt-1 text-xs text-slate-400">
                Connecting hearts, celebrating culture, building futures.
              </p>
            </footer>
          </section>
        </div>

        {/* Upgrade Modal - Redirect to Premium Page */}
        {showUpgradeModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="glass rounded-2xl p-8 max-w-md w-full">
              <h3 className="font-playfair text-2xl font-bold text-gray-900 mb-4">
                Upgrade to Elite
              </h3>
              <p className="text-gray-600 mb-6">
                Get extended access to the Lifestyle Lounge and all premium features.
              </p>
              <div className="space-y-4">
                <button
                  onClick={() => {
                    setShowUpgradeModal(false);
                    navigate('/premium');
                  }}
                  className="w-full bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-700 hover:to-pink-700 text-white px-6 py-4 rounded-xl font-semibold"
                >
                  View Elite Plans
                </button>
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-3 rounded-xl font-semibold"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Ticket 20: Lounge Limit Reached Modal */}
        {showLoungeLimitModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="w-full max-w-md rounded-2xl bg-slate-900 border border-amber-500/40 p-6 shadow-xl">
              <h2 className="text-lg font-semibold text-amber-400 mb-2 flex items-center gap-2">
                <Crown className="w-5 h-5" />
                Daily Lounge Limit Reached
              </h2>
              <p className="text-sm text-slate-300 mb-4">
                You've used your {entitlements?.limits?.lounge_minutes_per_day ?? entitlements?.daily_lounge_minutes ?? 20} minutes of free lounge time for today. 
                Upgrade to <span className="font-semibold text-amber-400">Elite</span> for extended access.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  className="px-3 py-1.5 rounded-lg text-sm text-slate-200 hover:bg-slate-800"
                  onClick={() => setShowLoungeLimitModal(false)}
                >
                  Close
                </button>
                <button
                  className="px-3 py-1.5 rounded-lg text-sm bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold flex items-center gap-1"
                  onClick={() => {
                    setShowLoungeLimitModal(false);
                    navigate('/profile/edit'); // Or your upgrade page
                  }}
                >
                  <Crown className="w-4 h-4" />
                  Upgrade to Elite
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      </LoungeBackground>
    );
  }

  // Inside lounge
  return (
    <LoungeBackground>
      {/* Phase 3.2: Root container with stable data-testid */}
      <div data-testid="lounge-root">
      {/* Phase 2: Reaction Bubbles Overlay (Neon) */}
      {activeReactions.map((reaction) => (
        <ReactionBubble
          key={reaction.id}
          emoji={reactionTypes[reaction.type] || reaction.emoji || reaction.type}
          xPercent={reaction.xPercent ?? 50}
          id={reaction.id}
        />
      ))}

      {/* Header - Responsive: Column on mobile, Row on desktop */}
      <div className="bg-black/90 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-3 md:py-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            {/* Left/Top: Lounge info + badges + controls */}
            <div className="flex items-center justify-between gap-2 md:gap-4">
              {/* Lounge name and participants */}
              <div className="flex items-center space-x-3 md:space-x-4">
                <Users className="w-5 h-5 md:w-6 md:h-6 text-rose-500" />
                <div>
                  {/* Ticket 22: Show active room in header */}
                  <h2 className="font-playfair text-lg md:text-xl font-bold text-white flex items-center gap-2">
                    <span>{activeRoom.emoji}</span>
                    <span>{activeRoom.label}</span>
                  </h2>
                  <div className="text-xs md:text-sm text-gray-400">{participants.length} participants</div>
                </div>
              </div>

              {/* Phase 3: Time & Streak Badges - Desktop (Neon) */}
              <div className="hidden md:flex items-center gap-3">
                <TimeBadge
                  minutesLeft={remainingMinutes}
                  isElite={isElite}
                />
                <StreakBadge
                  current={streakMeta?.current || 0}
                  best={streakMeta?.best || 0}
                />
              </div>

              {/* Heart button - Mobile only (moves to right side on desktop) */}
              <button
                onClick={() => {
                  console.log('❤️ Lounge heart clicked - favoriting room:', activeRoomKey);
                  // TODO: Implement favorite lounge functionality
                }}
                className="md:hidden p-2 rounded-full hover:bg-gray-700 text-pink-500 hover:text-pink-400 transition-colors"
                title="Favorite this lounge"
              >
                <Heart className="w-5 h-5" />
              </button>
            </div>
            
            {/* Right/Bottom: Controls (Heart + Leave on desktop, Leave only on mobile) */}
            <div className="flex items-center justify-between md:justify-end gap-2 md:gap-3">
              {/* Phase 3: Time & Streak Badges - Mobile (Neon) */}
              <div className="md:hidden flex flex-col gap-2">
                <TimeBadge
                  minutesLeft={remainingMinutes}
                  isElite={isElite}
                />
                <StreakBadge
                  current={streakMeta?.current || 0}
                  best={streakMeta?.best || 0}
                />
              </div>

              {/* Heart button - Desktop only */}
              <button
                onClick={() => {
                  console.log('❤️ Lounge heart clicked - favoriting room:', activeRoomKey);
                  // TODO: Implement favorite lounge functionality
                }}
                className="hidden md:block p-2 rounded-full hover:bg-gray-700 text-pink-500 hover:text-pink-400 transition-colors"
                title="Favorite this lounge"
              >
                <Heart className="w-5 h-5" />
              </button>
              
              {/* Leave button - Full width on mobile, auto on desktop */}
              <button
                onClick={() => setShowLeaveConfirm(true)}
                className="flex-1 md:flex-none bg-red-500 hover:bg-red-600 text-white px-4 md:px-6 py-2 rounded-lg md:rounded-lg font-semibold text-sm md:text-base text-center"
                title="Leave Lounge"
              >
                <span className="md:hidden">Leave</span>
                <span className="hidden md:inline">Leave Lounge</span>
              </button>
            </div>
          </div>

          {/* Ticket 22: Room Selector Tabs */}
          <div className="mt-4 flex flex-col gap-2">
            <div className="flex flex-wrap gap-2">
              {Object.values(LOUNGE_ROOMS).map((room) => {
                const isActive = room.key === activeRoomKey;
                const isLocked = room.key === 'after_dark' && !userIsAgeVerified;
                return (
                  <button
                    key={room.key}
                    type="button"
                    disabled={isSwitchingRoom}
                    onClick={() => {
                      if (isLocked) {
                        // Use canonical verification navigation with return URL
                        goVerify(navigate, location, null, { source: 'lounge_after_hours', isLoggedIn: !!user });
                      } else {
                        switchToRoom(room.key);
                      }
                    }}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-full border text-xs sm:text-sm transition
                      ${isActive
                        ? "bg-emerald-600 border-emerald-500 text-white shadow-sm"
                        : isLocked
                        ? "bg-slate-800 border-amber-500 text-slate-200 hover:bg-slate-700 opacity-75"
                        : "bg-slate-800 border-slate-600 text-slate-200 hover:bg-slate-700"
                      }`}
                  >
                    <span>{room.emoji}</span>
                    <span>{room.label}</span>
                    {isLocked && <span className="text-[10px]">🔒</span>}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-slate-400">
              {LOUNGE_ROOMS[activeRoomKey].description}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 pb-20 md:pb-4 grid grid-cols-1 lg:grid-cols-4 gap-3 sm:gap-4 lounge-main-content" style={{ height: 'calc(100dvh - 120px - var(--safe-area-inset-top, 0px))', minHeight: '480px' }}>
        {/* Main Area - Video & Controls */}
        <div className="lg:col-span-3 flex flex-col space-y-4 min-h-0">
          {/* Video Area - Fixed height to prevent shrinking when chat grows */}
          <div className="flex-none h-64 md:h-80 lg:h-[420px] bg-gray-900 rounded-xl p-4 flex flex-col overflow-hidden">
            {/* Header Section - always stays at top */}
            <div className="relative z-10 flex-shrink-0 flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <h2 className="text-white font-semibold text-lg">Lifestyle Lounge Video</h2>

                {/* Ticket 13: Connection health indicator */}
                {connectionHealth && (
                  <div className="hidden sm:flex items-center gap-1.5 text-xs text-gray-300">
                    <span
                      className={
                        'inline-flex h-2.5 w-2.5 rounded-full ' +
                        (connectionHealth.quality === 'good'
                          ? 'bg-emerald-400'
                          : connectionHealth.quality === 'degraded'
                          ? 'bg-yellow-400'
                          : connectionHealth.quality === 'poor'
                          ? 'bg-red-500'
                          : connectionHealth.quality === 'disconnected'
                          ? 'bg-gray-500'
                          : 'bg-gray-600')
                      }
                    />
                    <span>
                      {connectionHealth.quality === 'idle' && 'Not connected'}
                      {connectionHealth.quality === 'good' && 'Connection good'}
                      {connectionHealth.quality === 'degraded' && 'Connection okay'}
                      {connectionHealth.quality === 'poor' && 'Connection poor'}
                      {connectionHealth.quality === 'disconnected' && 'Disconnected'}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={isCameraOn ? handleTurnCameraOff : handleTurnCameraOn}
                  disabled={isCameraRequesting}
                  className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
                    isCameraOn
                      ? "bg-red-600 border-red-500 text-white hover:bg-red-700"
                      : "bg-emerald-600 border-emerald-500 text-white hover:bg-emerald-700"
                  } disabled:opacity-60 disabled:cursor-not-allowed`}
                >
                  {isCameraRequesting
                    ? "Starting..."
                    : isCameraOn
                    ? "Turn Camera Off"
                    : "Turn Camera On"}
                </button>

                {/* Skip button when we have a partner */}
                {currentPartnerId && (
                  <button
                    type="button"
                    onClick={handleSkipPartner}
                    className="px-3 py-1 rounded-full text-sm font-medium border border-slate-500 text-slate-100 bg-slate-800 hover:bg-slate-700"
                  >
                    Skip
                  </button>
                )}

                {/* Gift button when we have a partner */}
                {currentPartnerId && (() => {
                  const partner = participants.find(p => p.user_id === currentPartnerId);
                  const partnerName = partner?.first_name || partner?.display_name || partner?.name || 'Partner';
                  return (
                    <LoungeGiftButton
                      roomId={activeRoomKey}
                      participant={{
                        user_id: currentPartnerId,
                        first_name: partnerName,
                      }}
                    />
                  );
                })()}

                {/* Ticket 14: Moderation menu for current partner */}
                {currentPartnerId && (
                  <div className="relative">
                    <button
                      onClick={() => setShowPartnerMenu((v) => !v)}
                      className="ml-2 p-1 rounded-full hover:bg-white/5 text-white/70 text-sm"
                      title="More options"
                    >
                      •••
                    </button>
                    {showPartnerMenu && (
                      <div className="absolute right-0 mt-2 bg-slate-900 border border-white/10 rounded-lg shadow-lg text-xs z-20 min-w-[140px]">
                        <button
                          onClick={() => {
                            setShowPartnerMenu(false);
                            setShowLoungeReportModal(true);
                          }}
                          className="block w-full text-left px-3 py-2 hover:bg-white/5 text-white"
                        >
                          Report user
                        </button>
                        <button
                          onClick={async () => {
                            setShowPartnerMenu(false);
                            try {
                              await blockUser({ targetUserId: currentPartnerId });
                              alert('They won\'t be able to contact you again.');
                              if (skipPartner) skipPartner();
                            } catch (e) {
                              console.error(e);
                              alert('Failed to block user');
                            }
                          }}
                          className="block w-full text-left px-3 py-2 hover:bg-white/5 text-red-400"
                        >
                          Block user
                        </button>

                        {/* Admin-only kick (check if user.is_admin exists) */}
                        {user && user.is_admin && (
                          <button
                            onClick={async () => {
                              setShowPartnerMenu(false);
                              try {
                                await kickFromLounge({
                                  targetUserId: currentPartnerId,
                                  reason: 'admin_kick',
                                  hours: 72,
                                });
                                alert('User kicked from lounge for 72 hours');
                                if (skipPartner) skipPartner();
                              } catch (e) {
                                console.error(e);
                                alert('Failed to kick user');
                              }
                            }}
                            className="block w-full text-left px-3 py-2 hover:bg-white/5 text-orange-400"
                          >
                            Kick from lounge (72h)
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Error / permission message */}
            {webrtcStatus === 'error' && (
              <div className="flex-shrink-0 mb-3 text-xs text-red-300 bg-red-900/40 border border-red-700/60 rounded-lg px-3 py-2">
                Failed to access camera. Please check permissions.
              </div>
            )}

            {/* Video Container - fills remaining space OR fullscreen overlay */}
            <div 
              className={
                isVideoFullscreen
                  ? "fixed inset-0 z-[2147483647] bg-black"
                  : "relative flex-1 min-h-0 overflow-hidden rounded-xl bg-black"
              }
              style={isVideoFullscreen ? { height: '100dvh' } : undefined}
              data-testid="lounge-video-container"
            >
              {/* Exit button for fullscreen mode (mobile-friendly) */}
              {isVideoFullscreen && (
                <button
                  type="button"
                  onClick={() => setIsVideoFullscreen(false)}
                  className="absolute top-4 left-4 z-[2147483647] px-3 py-2 rounded-full bg-black/60 text-white border border-white/15 text-sm font-medium hover:bg-black/80 transition-colors"
                  data-testid="lounge-fullscreen-exit-btn"
                >
                  ← Exit
                </button>
              )}
              
              {isCameraOn ? (
                <LoungeVideoLayout
                  localVideoRef={localVideoRef}
                  remoteParticipants={
                    remoteStream && currentPartnerId
                      ? [{
                          id: currentPartnerId,
                          name:
                            participants.find(p => p.user_id === currentPartnerId)?.first_name ||
                            "Partner",
                          videoRef: remoteVideoRef,
                          isMuted: false,
                        }]
                      : []
                  }
                  isFullscreen={isVideoFullscreen}
                  onToggleFullscreen={() => setIsVideoFullscreen(v => !v)}
                />
              ) : (
                <div className="h-full flex items-center justify-center text-center text-gray-300 text-sm px-4 bg-gray-900 rounded-xl">
                  <div>
                    <p className="font-medium mb-1">Camera is off</p>
                    <p className="text-xs text-gray-400">
                      Tap <span className="font-semibold">"Turn Camera On"</span> to start your video.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Controls + Reactions - Unified Card */}
          <div className="mt-4 rounded-3xl bg-slate-950/90 px-4 py-4 border border-slate-800">
            {/* Mic + Camera Controls */}
            <div className="flex items-center justify-center gap-6 mb-3">
              <button
                onClick={toggleAudio}
                className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                  isAudioOn ? 'bg-slate-700 text-white hover:bg-slate-600' : 'bg-red-500 text-white hover:bg-red-600'
                }`}
                title={isAudioOn ? "Mute" : "Unmute"}
              >
                {isAudioOn ? <Mic className="w-6 h-6" /> : <MicOff className="w-6 h-6" />}
              </button>

              {/* 🔧 FIX: Only show camera toggle if room allows video */}
              {activeRoom.allowVideo && (
                <button
                  onClick={toggleVideo}
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                    isVideoOn ? 'bg-slate-700 text-white hover:bg-slate-600' : 'bg-red-500 text-white hover:bg-red-600'
                  }`}
                  title={isVideoOn ? "Turn Camera Off" : "Turn Camera On"}
                >
                  {isVideoOn ? <Video className="w-6 h-6" /> : <VideoOff className="w-6 h-6" />}
                </button>
              )}
            </div>

            {/* Reaction Picker - Spicy Edition */}
            <div className="flex justify-center pt-3 border-t border-slate-800/60 lounge-emoji-tray">
              <div className="flex gap-1.5 flex-wrap justify-center">
                <button onClick={() => sendReaction("heart")} className="text-xl hover:scale-125 transition-transform" title="Love">
                  ❤️
                </button>
                <button onClick={() => sendReaction("heartEyes")} className="text-xl hover:scale-125 transition-transform" title="Crush">
                  😍
                </button>
                <button onClick={() => sendReaction("fire")} className="text-xl hover:scale-125 transition-transform" title="Fire">
                  🔥
                </button>
                <button onClick={() => sendReaction("laugh")} className="text-xl hover:scale-125 transition-transform" title="Funny">
                  😂
                </button>
                <button onClick={() => sendReaction("nervous")} className="text-xl hover:scale-125 transition-transform" title="Haha">
                  😅
                </button>
                <button onClick={() => sendReaction("mindBlown")} className="text-xl hover:scale-125 transition-transform" title="Mind Blown">
                  🤯
                </button>
                <button onClick={() => sendReaction("hot")} className="text-xl hover:scale-125 transition-transform" title="Too Hot">
                  🥵
                </button>
                <button onClick={() => sendReaction("splash")} className="text-xl hover:scale-125 transition-transform" title="Yesss">
                  💦
                </button>
                <button onClick={() => sendReaction("eggplant")} className="text-xl hover:scale-125 transition-transform" title="Spicy">
                  🍆
                </button>
                <button onClick={() => sendReaction("devil")} className="text-xl hover:scale-125 transition-transform" title="Naughty">
                  😈
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar - Participants & Chat */}
        <div className="w-full md:w-1/3 flex flex-col gap-4 mt-4 md:mt-0 min-h-0 h-full">
          {/* Participants Card - Improved Mobile UX */}
          <section className="rounded-3xl bg-slate-950/95 border border-slate-800 px-4 py-3 flex-shrink-0">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-100">
                Participants ({participants.length})
              </p>

              {/* Mobile toggle */}
              <button
                type="button"
                className="md:hidden text-xs font-medium text-slate-400 underline hover:text-slate-300"
                onClick={() => setShowParticipants(prev => !prev)}
              >
                {showParticipants ? 'Hide' : 'Show'}
              </button>
            </div>

            {/* List: always visible on desktop, collapsible on mobile */}
            <div
              className={clsx(
                'mt-2 space-y-1.5 text-sm',
                showParticipants ? 'block max-h-40 overflow-y-auto' : 'hidden',
                'md:block'
              )}
            >
              {participants.map(p => {
                const displayName = p.first_name || p.display_name || p.name || p.username || 'Member';
                const isCurrentUser = p.user_id === user?.id || p.user_id === currentUserId;
                
                // Build usable profile: prefer backend profile, fallback from top-level fields
                const usableProfile = p.profile || {
                  display_name: p.display_name || displayName,
                  first_name: p.first_name || displayName,
                  avatar_url: p.avatar_url || null,
                  profile_photos: p.profile_photos || p.photos || [],
                  photos: p.photos || [],
                  gender: p.gender || p.sex || null,
                };
                
                const handleShowProfile = () => {
                  if (!isCurrentUser && usableProfile) {
                    profileViewer.showProfile({
                      display_name: usableProfile.display_name || displayName,
                      age: usableProfile.date_of_birth ? getAgeFromDob(usableProfile.date_of_birth) : usableProfile.age,
                      gender: usableProfile.gender,
                      relationship_style: usableProfile.relationship_style,
                      looking_for: usableProfile.looking_for || [],
                      location_city: usableProfile.location_city,
                      location_region: usableProfile.location_region,
                      bio: usableProfile.bio,
                      interests: usableProfile.interests || [],
                      photos: usableProfile.photos || [],
                      type: usableProfile.type,
                      background: usableProfile.background,
                    });
                  }
                };
                
                return (
                  <div key={p.user_id} className="flex items-center gap-3 bg-slate-900/70 rounded-lg px-3 py-2 hover:bg-slate-900 transition-colors">
                    {/* Avatar */}
                    <LoungeAvatar
                      profile={usableProfile}
                      size="md"
                      isSpeaking={p.isSpeaking || false}
                      isHighlighted={p.isHost || false}
                    />
                    
                    <div className="flex flex-col flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={handleShowProfile}
                          disabled={isCurrentUser}
                          className={clsx(
                            "truncate text-left text-slate-200 font-medium",
                            !isCurrentUser && "hover:text-pink-300 transition cursor-pointer"
                          )}
                        >
                          {displayName}
                        </button>
                        {/* Inner Circle / Elite badge */}
                        {(p.is_inner_circle || p.entitlements?.inner_circle?.active) && (
                          <span className="inline-flex items-center gap-0.5 rounded-full border border-yellow-500/30 bg-yellow-500/10 px-1.5 py-0.5 text-[9px] font-semibold text-yellow-200">
                            👑 IC
                          </span>
                        )}
                        {(p.entitlements?.elite?.active || p.entitlements?.elite_plus?.active) && !p.is_inner_circle && (
                          <span className="inline-flex items-center gap-0.5 rounded-full border border-purple-500/30 bg-purple-500/10 px-1.5 py-0.5 text-[9px] font-semibold text-purple-200">
                            ⭐ Elite
                          </span>
                        )}
                      </div>
                      {!isCurrentUser && (
                        <div className="flex gap-1 items-center mt-0.5">
                          <LoungeGiftButton roomId={activeRoomKey} participant={p} />
                          <button
                            onClick={() => chatRequests.sendRequest(p.user_id)}
                            disabled={chatRequests.isSending}
                            className="text-[10px] px-2 py-0.5 rounded-full border border-emerald-600 text-emerald-400 hover:bg-emerald-900/30 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Request 1:1 chat"
                          >
                            💬 Request chat
                          </button>
                        </div>
                      )}
                    </div>
                    <span className="ml-2 text-[10px] uppercase tracking-wide text-slate-400 flex-shrink-0">
                      {isCurrentUser ? 'You' : 'Guest'}
                    </span>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Chat Card - Improved Mobile UX */}
          <section className="rounded-3xl bg-slate-950/90 border border-slate-800 px-4 py-3 flex-1 min-h-0 overflow-hidden flex flex-col relative lounge-chat-section">
            <p className="text-xs font-semibold text-slate-100 mb-2">Chat</p>
            {/* Scrollable messages */}
            <div 
              ref={messagesContainerRef}
              className="flex-1 min-h-0 overflow-y-auto space-y-2 lounge-message-list"
              onScroll={handleChatScroll}
              data-testid="lounge-message-list"
            >
              {messages.map(msg => {
                  const nameToShow = msg.displayName || msg.first_name || msg.name || "Member";
                  const textToShow = msg.text || msg.content || "";
                  const isAudio = msg.type === 'audio' || msg.message_type === 'audio';
                  const audioUrl = msg.audio_url || msg.audioUrl;
                  
                  return (
                    <div key={msg.id} className="chat-message-enter rounded-lg px-3 py-2 bg-slate-900/70" data-testid="lounge-message-item">
                      <p className="text-xs font-semibold text-pink-300">
                        {nameToShow}
                      </p>

                      {/* Ticket 11: Audio message rendering */}
                      {isAudio && audioUrl ? (
                        <div className="flex items-center gap-2 mt-1">
                          <button
                            type="button"
                            className="flex items-center justify-center h-8 w-8 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white transition"
                            onClick={(e) => {
                              e.stopPropagation();
                              const audio = new Audio(`${BACKEND_URL}${audioUrl}`);
                              audio.play().catch(err => {
                                console.error("Failed to play audio:", err);
                              });
                            }}
                          >
                            ▶
                          </button>
                          <span className="text-xs text-slate-300">
                            Voice message · {Math.round((msg.duration_ms || 0) / 1000)}s
                          </span>
                        </div>
                      ) : (
                        <p className="text-sm text-slate-100 break-words">
                          {textToShow}
                        </p>
                      )}

                      {/* Ticket 9: Reactions row (summary) */}
                      {(msg.reactions && Object.keys(msg.reactions).length > 0) && (
                        <div className="mt-1 flex flex-wrap gap-1 text-xs">
                          {getReactionSummary(msg).map(({ emoji, count }) => (
                            <span
                              key={emoji}
                              className="px-1.5 py-0.5 rounded-full bg-white/10 text-white/90 flex items-center gap-1"
                            >
                              <span>{emoji}</span>
                              <span className="text-[10px]">{count}</span>
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Ticket 9 & 21: Reaction buttons (tap to react) */}
                      <div className="mt-1 flex gap-1 text-xs">
                        {REACTIONS.map((reaction) => {
                          const isMine = userHasReaction(msg, reaction.emoji, currentUserId);
                          return (
                            <button
                              key={reaction.key}
                              type="button"
                              onClick={() => toggleReaction(msg.id, reaction.emoji)}
                              className={`reaction-button reaction-pill px-1.5 py-0.5 rounded-full border ${
                                isMine
                                  ? 'bg-emerald-600/20 border-emerald-500/60'
                                  : 'border-white/10 bg-white/0 hover:bg-white/10'
                              }`}
                            >
                              {reaction.emoji}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              <div ref={messagesEndRef} />
            </div>

            {/* Ticket 7: "New messages" pill - shown when user scrolls up and new messages arrive */}
            {hasNewMessages && !isAtBottom && (
              <button
                onClick={handleScrollToBottom}
                className="absolute bottom-16 right-4 z-20 px-3 py-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs shadow-lg flex items-center gap-1 transition-all"
              >
                <span>New messages</span>
                <span>↓</span>
              </button>
            )}

            {/* Ticket 8: Typing indicator - shown when someone is typing */}
            {Object.keys(typingUsers).length > 0 && (
              <div className="px-3 pb-1 pt-0 flex justify-start">
                <TypingIndicator />
              </div>
            )}

            {/* Ticket 11: Recording status bar */}
            {isRecordingAudio && (
              <div className="mx-3 mb-2 flex items-center justify-between text-xs text-red-300 bg-red-900/40 border border-red-700/60 rounded-lg px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                  <span>Recording voice message… {Math.max(1, Math.round(audioDurationMs / 1000))}s</span>
                </div>
                <button
                  type="button"
                  onClick={handleCancelRecording}
                  className="text-[11px] uppercase tracking-wide font-semibold text-red-200 hover:text-red-100"
                >
                  Cancel
                </button>
              </div>
            )}

            {audioError && (
              <div className="mx-3 mb-2 text-[11px] text-red-300 bg-red-900/20 border border-red-700/40 rounded px-2 py-1">
                {audioError}
              </div>
            )}

            {/* Input row pinned to bottom of chat panel */}
            <form
              onSubmit={sendMessage}
              className="border-t border-slate-800 px-3 py-2 flex items-center gap-2 bg-slate-900 flex-shrink-0 lounge-composer"
            >
              <input
                  value={messageInput}
                  onChange={e => {
                    setMessageInput(e.target.value);
                    if (e.target.value.trim()) sendTypingIndicator();
                  }}
                  placeholder={isRecordingAudio ? "Recording voice message..." : "Type a message..."}
                  className="flex-1 bg-slate-800 rounded-full px-3 py-2 text-sm text-slate-100 focus:outline-none disabled:opacity-50"
                  disabled={isRecordingAudio}
                  data-testid="lounge-composer-input"
                />

              {/* Ticket 11: Mic button for voice messages */}
              <button
                type="button"
                onClick={handleMicClick}
                className={`flex items-center justify-center h-9 w-9 rounded-full border transition-colors ${
                  isRecordingAudio
                    ? "bg-red-600 border-red-500 text-white animate-pulse"
                    : "bg-slate-700 border-slate-600 text-slate-200 hover:bg-slate-600"
                }`}
                title={isRecordingAudio ? "Tap to stop and send" : "Tap to record voice message"}
              >
                {isRecordingAudio ? (
                  <span className="text-[10px] font-bold">REC</span>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                    />
                  </svg>
                )}
              </button>

              <button
                  type="button"
                  onClick={sendMessage}
                  disabled={isRecordingAudio || !messageInput.trim() || isSending}
                  className="px-3 py-2 rounded-full text-sm font-semibold bg-pink-500 hover:bg-pink-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="lounge-send-btn"
                >
                {isSending ? '...' : 'Send'}
              </button>
            </form>
          </section>
        </div>
      </div>

      {/* TODO: Re-add Low-Time Warning Toast & Paywall Modal after Phase 1 testing */}

      {/* Ticket 14: Lounge report modal */}
      {showLoungeReportModal && currentPartnerId && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-slate-900 rounded-xl p-6 w-full max-w-md border border-white/10 shadow-xl">
            <h3 className="text-white font-semibold text-lg mb-2">Report user</h3>
            <p className="text-sm text-white/70 mb-4">
              Tell us what happened during this lounge session.
            </p>
            <textarea
              value={loungeReportReason}
              onChange={(e) => setLoungeReportReason(e.target.value)}
              className="w-full h-32 rounded-lg bg-black/40 border border-white/10 text-sm text-white px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="Reason (required)"
            />
            <div className="flex justify-end gap-3 text-sm">
              <button
                onClick={() => {
                  setShowLoungeReportModal(false);
                  setLoungeReportReason('');
                }}
                className="px-4 py-2 rounded-lg bg-slate-800 text-white/70 hover:bg-slate-700"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!loungeReportReason.trim()) {
                    alert('Please enter a reason');
                    return;
                  }
                  try {
                    await reportUser({
                      targetUserId: currentPartnerId,
                      reason: loungeReportReason,
                      matchId: null,
                      messageId: null,
                      loungeRoom: 'lounge:lifestyle:main',
                    });
                    setShowLoungeReportModal(false);
                    setLoungeReportReason('');
                    alert('Thanks — our team will review this shortly.');
                  } catch (e) {
                    console.error(e);
                    alert('Failed to submit report');
                  }
                }}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700"
              >
                Submit report
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ticket 27: Gift Toast Notification */}
      {giftToast && (
        <LoungeGiftToast event={giftToast} onDone={() => setGiftToast(null)} />
      )}

      {/* P2: Profile View Modal */}
      <ProfileViewModal
        open={profileViewer.open}
        onClose={profileViewer.close}
        profile={profileViewer.profile}
        isSelf={profileViewer.isSelf}
        source={profileViewer.source}
        onLikeFromProfile={profileViewer.onLikeFromProfile}
        onPassFromProfile={profileViewer.onPassFromProfile}
      />

      {/* Phase 4: Leave Confirmation Modal */}
      {showLeaveConfirm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <h3 className="font-playfair text-xl font-bold text-white mb-3">
              Leave the Lounge?
            </h3>
            <p className="text-gray-300 text-sm mb-6">
              You can come back anytime while you still have minutes left today.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLeaveConfirm(false)}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-3 rounded-xl font-semibold transition-colors"
              >
                Stay in Lounge
              </button>
              <button
                onClick={async () => {
                  setShowLeaveConfirm(false);
                  await leaveLounge();
                }}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-gray-200 px-4 py-3 rounded-xl font-semibold transition-colors"
              >
                Leave
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phase 4: First-Time Time Explainer */}
      {showTimeExplainer && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 px-4 w-full max-w-md">
          <div className="bg-emerald-900/95 border border-emerald-500/60 rounded-2xl p-4 shadow-2xl">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xl">⏱️</span>
                  <h4 className="font-semibold text-emerald-100">How Lounge time works</h4>
                </div>
                <p className="text-sm text-emerald-50 leading-relaxed">
                  You get a set amount of Lounge time each day. 
                  Your available minutes reset daily.
                </p>
              </div>
              <button
                onClick={dismissExplainer}
                className="text-emerald-200 hover:text-white transition-colors p-1"
                aria-label="Dismiss"
              >
                <span className="text-xl">×</span>
              </button>
            </div>
            <button
              onClick={dismissExplainer}
              className="mt-3 w-full bg-emerald-700 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Request Chat V1: Incoming Chat Request Modal */}
      <ChatRequestModal
        request={chatRequests.incomingRequest}
        onAccept={async () => {
          const result = await chatRequests.respondToRequest(
            chatRequests.incomingRequest.id,
            'accept'
          );
          if (result.success) {
            // Navigate to messages with this user
            navigate(`/messages?userId=${chatRequests.incomingRequest.from_user_id}`);
          }
        }}
        onDecline={async () => {
          await chatRequests.respondToRequest(
            chatRequests.incomingRequest.id,
            'decline'
          );
        }}
        isResponding={chatRequests.isResponding}
      />
      
      {/* Post-Event Reflection Modal */}
      <PostEventReflectionModal
        open={showReflectionModal}
        onClose={handleReflectionClose}
        eventType="lounge"
        eventId={activeRoomKey}
      />
      </div>{/* End lounge-root */}
    </LoungeBackground>
  );
};

export default LifestyleLounge;
